import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import {
    Box,
    createStyles, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, Icon,
    IconButton, Input, InputAdornment, InputLabel, makeStyles, MenuItem, Radio, RadioGroup, Select, TextField, Theme, Typography
} from '@material-ui/core';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import ptBR from 'date-fns/locale/pt-BR';

import { InfluencerContext } from '../NewsContext';

import { ClassificationInfluencerModel } from '../../../Models/ClassificationInfluencerModel';
import { HomeContext } from '../../Home/HomeContext';
import { NewsLoadingContext } from '../NewsLoadingContext';
import { InfluencerModel } from '../../../Models/InfluencerModel';
import { format } from 'date-fns';
import { InfluencersAuxiliarResponse } from '../../../Models/InfluencerAuxiliarModel';
import InfluencerService from '../../../Services/InfluencerService';
import { theme } from '../../../theme';
import { Autocomplete } from '@material-ui/lab';

const useStyles = makeStyles((theme: Theme) => createStyles({
    itens: {
        width: '100%',
        color: 'primary',
    },
    itemData: {
        width: '100%',
        color: 'primary',
        paddingBottom: theme.spacing(1.5),

    },
    linkColor: {
        color: '#2C64D0',
        textOverflow: 'ellipsis',
        '&:hover': {
            textDecoration: 'underline',
            cursor: 'pointer',
        },
    },
    editText: {
        textOverflow: 'ellipsis',
    },
    yellowBackground: {
        backgroundColor: theme.palette.warning.light
    },
    outsideGrid: {
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    loading: {
        minHeight: '20vh'
    },
    linksPaperError: {
        padding: theme.spacing(3),
        border: `1px solid ${theme.palette.error.main}`,
    },
    errorText: {
        paddingLeft: theme.spacing(2)
    }
}));
interface NewsDataProps {
    news: InfluencerModel | null;
    setNews: (news: InfluencerModel) => void;
    newsCurrentId: number | null;
    setNewsCurrentId: (currentId: number | null) => void;
}

export function validateInfluencerData(news: InfluencerModel) {
    const errors: any = {};
    if (!news.marcaId) errors.marcaId = true;
    if (!news.areaId) errors.areaId = true;
    if (!news.vpId) errors.vpId = true;
    if (!news.nome || !news.nome.trim()) errors.nome = true;
    if (!news.url_perfil || !news.url_perfil.trim()) errors.url_perfil = true;
    if (!news.iniciativa || !news.iniciativa.trim()) errors.iniciativa = true;
    if (!news.objetivoContratacao || !news.objetivoContratacao.trim()) errors.objetivoContratacao = true;
    if (!news.finalidadeId) errors.finalidadeId = true;
    if (!news.formatoId) errors.formatoId = true;
    if (!news.canaisId || !Array.isArray(news.canaisId) || news.canaisId.length === 0) errors.canaisId = true;
    if (!news.abrangenciaId) errors.abrangenciaId = true;
    return errors;
}

type ErrosFormulario = Partial<Record<keyof InfluencerModel, string>>;
export default function InfluencerData({ news, setNews, newsCurrentId, setNewsCurrentId }: NewsDataProps) {
    const classes = useStyles();

    const newsLoadingContext = React.useContext(NewsLoadingContext);
    const homeContext = React.useContext(HomeContext);
    const clientEnable = homeContext.influencerClientEnable;
    const [filters, setFilters] = React.useState<InfluencersAuxiliarResponse>();
    const [erros, setErros] = React.useState<ErrosFormulario>({} as ErrosFormulario);
    const [loading, setLoading] = React.useState(false);

    const [nomeDisabled, setNomeDisabled] = React.useState(true);
    const [nomeFocused, setNomeFocused] = React.useState(false);

    const [urlPerfilDisabled, setUrlPerfilDisabled] = React.useState(true);
    const [urlPerfilFocused, setUrlPerfilFocused] = React.useState(false);

    const [iniciativaDisabled, setIniciativaDisabled] = React.useState(true);
    const [iniciativaFocused, setIniciativaFocused] = React.useState(false);

    const [objetivoContratacaoDisabled, setObjetivoContratacaoDisabled] = React.useState(true);
    const [objetivoContratacaoFocused, setObjetivoContratacaoFocused] = React.useState(false);



    React.useEffect(() => {
        setLoading(true);
        if (clientEnable) {
            InfluencerService.getInfluencersBrandByClient(clientEnable.id_cliente)
                .then((res: AxiosResponse) => {
                    setFiltersData(res.data);

                    setLoading(false);
                })
                .catch((error: AxiosError) => { setLoading(false); homeContext.showError('', error); });
        }

    }, []);

    const setFiltersData = (data: InfluencersAuxiliarResponse) => {
        setFilters(data);
    };

    const handleChange = (prop: keyof ClassificationInfluencerModel) => (event: React.ChangeEvent<HTMLInputElement>) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        setNews({ ...news, [prop]: event.target.value });
    };

    const handleChangeSelect = (prop: keyof ClassificationInfluencerModel) =>
        (event: React.ChangeEvent<{ value: unknown }>) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            setNews({ ...news, [prop]: event.target.value as string });
        };

    const handleChangeSelectCanais = (prop: keyof ClassificationInfluencerModel) =>
        (event: React.ChangeEvent<{ value: unknown }>) => {
            const value = event.target.value;
            // value pode ser um array de ids (ex: [1,2,3])
            const canaisArray = Array.isArray(value)
                ? value.map((id_aux) => ({ id_aux }))
                : [];
            setNews({ ...(news as InfluencerModel), [prop]: canaisArray });
        };

    const validarCampos = (): ErrosFormulario => {
        const novosErros: ErrosFormulario = {};

        const camposObrigatorios: (keyof InfluencerModel)[] = [
            'area',
            'marca',
            'vp',
            'nome',
            'url_perfil',
            'iniciativa',
            'objetivoContratacao',
            'finalidade',
            'formato',
            'canais',
            'abrangencia',
        ];

        camposObrigatorios.forEach((campo) => {
            if (news) {
                const valor = news[campo];
                if (typeof valor === 'string' && !valor.trim()) {
                    novosErros[campo] = 'Campo obrigatório.';
                } else if (valor === null || valor === undefined) {
                    novosErros[campo] = 'Campo obrigatório.';
                }
            }
        });
        return novosErros;
    };

    const getDate = () => {
        return (
            <Grid item xs={6}>
                <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
                    <DatePicker
                        id="news-date"
                        variant="inline"
                        value={news?.dataAnalise}
                        inputVariant="outlined"
                        label="Data de upload"
                        minDate={new Date('0000-00-00')}
                        onChange={(date) =>
                            date !== null ? '' : ''
                        }
                        InputProps={{
                            readOnly: true,
                        }}
                        format="dd/MM/yyyy"
                        className={classes.itemData}
                    />
                </MuiPickersUtilsProvider>
            </Grid>
        );
    };

    const getNomeSolicitante = () => {
        return (
            <>
                <Grid item xs={4}>
                    <TextField
                        id="nomeSolicitante"
                        value={news?.id_solicitante}
                        onChange={() => { return; }}
                        label="Nome Solicitante"
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                        }}
                        className={classes.itens} />
                </Grid>
            </>
        );
    };

    const getResponsavelReputacao = () => {
        return (
            <>
                <Grid item xs={4}>
                    <TextField
                        id="responsavelReputacao"
                        value={news?.responsavelReputacao}
                        onChange={() => { return; }}
                        label="Responsável Reputação"
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                        }}
                        className={classes.itens} />
                </Grid>
            </>
        );
    };

    const getEmail = () => {
        return (
            <>
                <Grid item xs={4}>
                    <TextField
                        id="E-mail"
                        value={news?.email}
                        onChange={() => { return; }}
                        label="E-mail"
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                        }}
                        className={classes.itens} />
                </Grid>
            </>
        );
    };

    const getDataRecebimento = () => {
        const dataAtualFormatada = news?.dataRecebimento ? format(new Date(news.dataRecebimento), 'dd/MM/yyyy') : '';
        return (
            <Grid item xs={4}>
                <TextField
                    id="news-date"
                    label="Data"
                    value={dataAtualFormatada}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        readOnly: true,
                    }}
                    fullWidth
                />
            </Grid>
        );
    };



    const getNomeFiguraPublica = () => {
        return (
            <>
                <Grid item xs={4}>
                    <TextField
                        id="nomeFiguraPublica"
                        value={news?.nome}
                        onChange={handleChange('nome')}
                        label="Nome"
                        variant="outlined"
                        disabled={nomeDisabled}
                        className={classes.itens}
                        onBlur={() => { setNomeDisabled(true); setNomeFocused(false); }}
                        inputRef={input => nomeFocused && input && input.focus()}
                        InputProps={{
                            endAdornment: (nomeDisabled &&
                                <InputAdornment position="end">
                                    <IconButton onClick={() => { setNomeDisabled(!nomeDisabled); setNomeFocused(true); }}>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </InputAdornment>)
                        }}
                    />
                    {erros.nome && (
                        <FormHelperText>Este campo é obrigatório.</FormHelperText>
                    )}
                </Grid>
            </>
        );
    };

    const getObservacoes = () => {
        return (
            <>
                <Grid item xs={12}>
                    <TextField
                        id="observacaoes"
                        value={news?.observacoes || ''}
                        onChange={handleChange('observacoes')}
                        label="Observações"
                        variant="outlined"
                        multiline
                        minRows={3}
                        maxRows={3}
                        InputLabelProps={{ shrink: true }}
                        className={classes.itens} />
                </Grid>
            </>
        );
    };
    const getUrlPerfil = () => {
        return (
            <>
                <Grid item xs={4}>
                    <TextField
                        id="URL da rede social principal"
                        value={news?.url_perfil}
                        onChange={handleChange('url_perfil')}
                        label="URL da rede social principal"
                        variant="outlined"
                        disabled={urlPerfilDisabled}
                        className={classes.itens}
                        onBlur={() => { setUrlPerfilDisabled(true); setUrlPerfilFocused(false); }}
                        inputRef={input => nomeFocused && input && input.focus()}
                        InputProps={{
                            endAdornment: (nomeDisabled &&
                                <InputAdornment position="end">
                                    <IconButton onClick={() => { setUrlPerfilDisabled(!nomeDisabled); setUrlPerfilFocused(true); }}>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </InputAdornment>)
                        }}
                    />
                    {erros.url_perfil && (
                        <FormHelperText>Este campo é obrigatório.</FormHelperText>
                    )}
                </Grid>
            </>
        );
    };

    const selectMarca = () => {
        const isEmpty = news?.marcaId === null || news?.marcaId === undefined || news?.marcaId === 0;
        return (
            <Grid item xs={4}>
                <FormControl
                    variant="outlined"
                    style={{ width: '100%' }}
                    required
                    error={!!erros.marcaId || isEmpty}
                >
                    <Autocomplete
                        id="marca-autocomplete"
                        options={filters?.marcas ?? []}
                        getOptionLabel={(option) => option.descricao || ''}
                        value={filters?.marcas?.find(m => m.id_aux === news?.marcaId) || null}
                        onChange={(_, newValue) => {
                            if (!news) return;
                            setNews({
                                ...news,
                                marcaId: newValue ? newValue.id_aux : 0,
                                id: news.id ?? 0
                            });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Marca"
                                variant="outlined"
                                error={!!erros.marcaId}
                                required
                            />
                        )}
                    />
                    {((erros.marcaId || isEmpty)) && (
                        <FormHelperText>Este campo é obrigatório.</FormHelperText>
                    )}
                </FormControl>
            </Grid>
        );
    };

    const selectArea = () => {
        const isEmpty = news?.areaId === null || news?.areaId === undefined || news?.areaId === 0;
        return (
            <Grid item xs={4}>
                <FormControl
                    variant="outlined"
                    style={{ width: '100%' }}
                    required
                    error={!!erros.areaId || isEmpty}
                >
                    <Autocomplete
                        id="area-autocomplete"
                        options={filters?.area ?? []}
                        getOptionLabel={(option) => option.descricao || ''}
                        value={filters?.area?.find(a => a.id_aux === news?.areaId) || null}
                        onChange={(_, newValue) => {
                            if (!news) return;
                            setNews({
                                ...news,
                                areaId: newValue ? newValue.id_aux : 0,
                                id: news.id ?? 0
                            });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Área"
                                variant="outlined"
                                error={!!erros.area}
                                required
                            />
                        )}
                    />
                    {(erros.area || isEmpty) && (
                        <FormHelperText>Este campo é obrigatório.</FormHelperText>
                    )}
                </FormControl>
            </Grid>
        );
    };

    const selectVp = () => {
        const isEmpty = news?.vpId === null || news?.vpId === undefined || news?.vpId === 0;
        return (
            <Grid item xs={4}>
                <FormControl
                    variant="outlined"
                    style={{ width: '100%' }}
                    required
                    error={!!erros.vpId || isEmpty}
                >
                    <Autocomplete
                        id="vp-autocomplete"
                        options={filters?.vp ?? []}
                        getOptionLabel={(option) => option.descricao || ''}
                        value={filters?.vp?.find(v => v.id_aux === news?.vpId) || null}
                        onChange={(_, newValue) => {
                            if (!news) return;
                            setNews({
                                ...news,
                                vpId: newValue ? newValue.id_aux : 0,
                                id: news.id ?? 0
                            });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="VP"
                                variant="outlined"
                                error={!!erros.vpId}
                                required
                            />
                        )}
                    />
                    {(erros.vpId || isEmpty) && (
                        <FormHelperText>Este campo é obrigatório.</FormHelperText>
                    )}
                </FormControl>
            </Grid>
        );
    };


    const selectFinalidade = () => {
        const isEmpty = news?.finalidadeId === null || news?.finalidadeId === undefined || news?.finalidadeId === 0;
        return (
            <Grid item xs={4}>
                <FormControl
                    variant="outlined"
                    style={{ width: '100%' }}
                    required error={!!erros.finalidadeId || isEmpty}
                >
                    <Autocomplete
                        id="finalidade-autocomplete"
                        options={filters?.finalidades ?? []}
                        getOptionLabel={(option) => option.descricao || ''}
                        value={filters?.finalidades?.find(f => f.id_aux === news?.finalidadeId) || null}
                        onChange={(_, newValue) => {
                            if (!news) return;
                            setNews({
                                ...news,
                                finalidadeId: newValue && typeof newValue.id_aux === 'number' ? newValue.id_aux : 0,
                                id: news.id ?? 0
                            });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Finalidade"
                                variant="outlined"
                                error={!!erros.finalidadeId || isEmpty}
                                required
                            />
                        )}
                    />
                    {(erros.finalidadeId || isEmpty) && (
                        <FormHelperText error>Este campo é obrigatório.</FormHelperText>
                    )}
                </FormControl>
            </Grid>
        );
    };
    const selectFormato = () => {
        const isEmpty = news?.formatoId === null || news?.formatoId === undefined || news?.formatoId === 0;
        return (
            <Grid item xs={4}>
                <FormControl
                    variant="outlined"
                    style={{ width: '100%' }}
                    required error={!!erros.formatoId || isEmpty}
                >
                    <Autocomplete
                        id="formato-autocomplete"
                        options={filters?.formato ?? []}
                        getOptionLabel={(option) => option.descricao || ''}
                        value={filters?.formato?.find(f => f.id_aux === news?.formatoId) || null}
                        onChange={(_, newValue) => {
                            if (!news) return;
                            setNews({
                                ...news,
                                formatoId: newValue && typeof newValue.id_aux === 'number' ? newValue.id_aux : 0,
                                id: news.id ?? 0
                            });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Formato"
                                variant="outlined"
                                error={!!erros.formatoId || isEmpty}
                                required
                            />
                        )}
                    />
                    {(erros.formatoId || isEmpty) && (
                        <FormHelperText>Este campo é obrigatório.</FormHelperText>
                    )}
                </FormControl>
            </Grid>
        );
    };

    const selectCanais = () => {
        const isEmpty = news?.canaisId === null || news?.canaisId === undefined;

        return (
            <Grid item xs={4}>
                <FormControl
                    variant="outlined"
                    style={{ width: '100%' }}
                    required
                    error={!!erros.canaisId || isEmpty}
                >
                    <Autocomplete
                        multiple
                        id="canais-autocomplete"
                        options={filters?.canais ?? []}
                        getOptionLabel={(option) => option.descricao || ''}
                        value={filters?.canais?.filter((c) => Array.isArray(news?.canaisId) && news?.canaisId?.some((n) => n.id_aux === c.id_aux)) || []}
                        onChange={(_, newValue) => {
                            if (!news) return;
                            setNews({
                                ...news,
                                canaisId: newValue
                                    .filter((c) => typeof c.id_aux === 'number')
                                    .map((c) => ({ id_aux: c.id_aux as number })),
                            });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Canais de divulgação"
                                variant="outlined"
                                error={!!erros.canaisId || isEmpty}
                                required
                            />
                        )}
                        disableCloseOnSelect
                    />
                    {(!!erros.canaisId || isEmpty || (Array.isArray(news?.canaisId) && news.canaisId.length === 0)) && (
                        <FormHelperText error>Este campo é obrigatório.</FormHelperText>
                    )}
                </FormControl>
            </Grid>
        );
    };
    const selectAbrangencia = () => {
        const isEmpty = news?.abrangenciaId === null || news?.abrangenciaId === undefined;
        return (
            <Grid item xs={4}>
                <FormControl
                    variant="outlined"
                    style={{ width: '100%' }}
                    required error={!!erros.abrangenciaId || isEmpty}
                >
                    <Autocomplete
                        id="abrangencia-autocomplete"
                        options={filters?.abrangencia ?? []}
                        getOptionLabel={(option) => option.descricao || ''}
                        value={filters?.abrangencia?.find(a => a.id_aux === news?.abrangenciaId) || null}
                        onChange={(_, newValue) => {
                            if (!news) return;
                            setNews({
                                ...news,
                                abrangenciaId: newValue && typeof newValue.id_aux === 'number' ? newValue.id_aux : 0,
                                id: news.id ?? 0
                            });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Abrangência"
                                variant="outlined"
                                error={!!erros.abrangenciaId || isEmpty}
                                required
                            />
                        )}
                    />
                    {(erros.abrangenciaId || isEmpty) && (
                        <FormHelperText>Este campo é obrigatório.</FormHelperText>
                    )}
                </FormControl>
            </Grid>
        );
    };


    const getIniciativa = () => {
        return (
            <>
                <Grid item xs={4}>
                    <TextField
                        id="iniciativa"
                        value={news?.iniciativa}
                        label="Iniciativa"
                        variant="outlined"
                        onChange={handleChange('iniciativa')}
                        disabled={iniciativaDisabled}
                        className={classes.itens}
                        onBlur={() => { setIniciativaDisabled(true); setIniciativaFocused(false); }}
                        inputRef={input => nomeFocused && input && input.focus()}
                        InputProps={{
                            endAdornment: (nomeDisabled &&
                                <InputAdornment position="end">
                                    <IconButton onClick={() => { setIniciativaDisabled(!nomeDisabled); setIniciativaFocused(true); }}>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </InputAdornment>)
                        }}
                    />
                    {erros.iniciativa && (
                        <FormHelperText>Este campo é obrigatório.</FormHelperText>
                    )}
                </Grid>
            </>
        );
    };

    const getObjetivoContratacao = () => {
        return (
            <>
                <Grid item xs={4}>
                    <TextField
                        id="objetivoContratacao"
                        value={news?.objetivoContratacao}
                        label="Objetivo da Contratação"
                        variant="outlined"
                        onChange={handleChange('objetivoContratacao')}
                        disabled={objetivoContratacaoDisabled}
                        className={classes.itens}
                        onBlur={() => { setObjetivoContratacaoDisabled(true); setObjetivoContratacaoFocused(false); }}
                        inputRef={input => objetivoContratacaoFocused && input && input.focus()}
                        InputProps={{
                            endAdornment: (objetivoContratacaoDisabled &&
                                <InputAdornment position="end">
                                    <IconButton onClick={() => { setObjetivoContratacaoDisabled(!objetivoContratacaoDisabled); setObjetivoContratacaoFocused(true); }}>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </InputAdornment>)
                        }}
                    />
                    {erros.objetivoContratacao && (
                        <FormHelperText>Este campo é obrigatório.</FormHelperText>
                    )}
                </Grid>
            </>
        );
    };


    return (
        <>
            <Grid>
                <Grid
                    container
                    spacing={3}
                    alignItems="flex-start" >
                    {getNomeSolicitante()}
                    {getResponsavelReputacao()}
                    {getEmail()}
                    {getDataRecebimento()}
                    {getNomeFiguraPublica()}
                    {getUrlPerfil()}
                    {getIniciativa()}
                    {getObjetivoContratacao()}
                    {selectMarca()}
                    {selectArea()}
                    {selectVp()}
                    {selectFinalidade()}
                    {selectFormato()}
                    {selectCanais()}
                    {selectAbrangencia()}
                    {getObservacoes()}
                </Grid>
            </Grid>
        </>
    );
}